import { useRef, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NAV_ICONS } from '@/lib/ui/navIcons';
import { PHONE_TAB_ROOTS, activeTabFor, isTabBarHidden, type PhoneTab } from '@/lib/ui/phoneNav';
import { useKeyboardOpen } from '@/lib/ui/keyboard';
import { useBottomChrome } from '@/lib/ui/bottomChrome';
import { scrollAppContentToTop } from '@/lib/ui/appScroll';
import { waitingLabel } from '@/lib/ui/waitingLabel';
import { unreadLabel } from '@/lib/ui/unreadLabel';
import { CountBadge, formatCount } from '@/components/ui/count-badge';

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface TabDef {
  id: PhoneTab;
  label: string;
  icon: IconComponent;
  activeIcon: IconComponent;
}

/** Rendered order (DESIGN.md › BottomTabBar). */
const TABS: ReadonlyArray<TabDef> = [
  { id: 'chats', label: 'Chats', icon: NAV_ICONS.chats.outline, activeIcon: NAV_ICONS.chats.solid },
  { id: 'tasks', label: 'Tasks', icon: NAV_ICONS.tasks.outline, activeIcon: NAV_ICONS.tasks.solid },
  { id: 'boards', label: 'Boards', icon: NAV_ICONS.boards.outline, activeIcon: NAV_ICONS.boards.solid },
  { id: 'more', label: 'More', icon: NAV_ICONS.more.outline, activeIcon: NAV_ICONS.more.solid },
];

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

/**
 * Story 24.7 — phone and tablet navigation (<1024px): Chats, Tasks, Boards,
 * More, as the LAST row of MainLayout's flow column.
 *
 * - In flow, `sticky bottom-0`: never `fixed`, never translated off-screen.
 * - 🔴 The `<nav>` carries NO display utility except `lg:hidden` (layout lives
 *   on the inner `<ul>`), so the `hidden` attribute always wins. A `flex` on
 *   this element would override the UA `display: none`.
 * - Hidden + `inert` only through `isTabBarHidden` — the ONE hide path.
 *   Story 24.7b feeds it the keyboard-open signal (`useKeyboardOpen`).
 * - Re-tapping the active tab returns to its root and scrolls to the top
 *   (`scrollAppContentToTop`: `<main data-app-scroll>` is the scroller).
 * - Story 24.7b — bottom safe area via `pb-safe-bottom`, and the bar
 *   registers as bottom chrome (`--bottom-chrome`) while shown, so the phone
 *   toast sits above it.
 * - Story 24.18 — the Tasks tab (only) carries the waiting count as the
 *   shared `CountBadge`, at the icon's top-right with a 2px `ring-surface`.
 *   `waitingCount` comes from `MainLayout` (the one `useBriefing` reader);
 *   while the badge shows, the link is named "Tasks, N waiting"
 *   (`waitingLabel`, the real number even at "9+"). No badge at 0, while
 *   loading or on a briefing error, and no live region.
 * - Story 29.2 — the Chats tab carries the unread-conversation count the same
 *   way (`unreadCount`, also from `MainLayout`, same `ring-2 ring-surface`),
 *   named "Chats, N unread" (`unreadLabel`). Each tab names itself with its
 *   own label function; Boards and More carry no badge.
 */
export default function BottomTabBar({
  waitingCount,
  unreadCount,
}: {
  waitingCount?: number;
  unreadCount?: number;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const active = activeTabFor(location.pathname);
  const keyboardOpen = useKeyboardOpen();
  const hidden = isTabBarHidden(location, keyboardOpen);
  const navRef = useRef<HTMLElement>(null);
  useBottomChrome(navRef, 'tabbar', !hidden);

  const handleActiveTap = (event: MouseEvent<HTMLAnchorElement>, root: string) => {
    // A modified or non-primary click (new tab / window) is the Link's to handle.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    if (location.pathname + location.search !== root) navigate(root);
    scrollAppContentToTop(prefersReducedMotion() ? 'auto' : 'smooth');
  };

  return (
    <nav
      ref={navRef}
      aria-label="Main"
      data-bottom-tab-bar
      hidden={hidden}
      inert={hidden}
      className="lg:hidden sticky bottom-0 z-20 bg-surface border-t border-line pb-safe-bottom"
    >
      <ul role="list" className="flex min-h-14 items-stretch">
        {TABS.map((tab) => {
          const isActive = tab.id === active;
          const root = PHONE_TAB_ROOTS[tab.id];
          const Icon = isActive ? tab.activeIcon : tab.icon;
          const badgeCount =
            tab.id === 'tasks' ? waitingCount : tab.id === 'chats' ? unreadCount : undefined;
          const nameFor = tab.id === 'chats' ? unreadLabel : waitingLabel;
          const hasBadge = formatCount(badgeCount) !== null;
          return (
            <li key={tab.id} className="flex flex-1 min-w-0">
              <Link
                to={root}
                aria-current={isActive ? 'page' : undefined}
                aria-label={hasBadge ? nameFor(tab.label, badgeCount) : undefined}
                onClick={isActive ? (event) => handleActiveTap(event, root) : undefined}
                className={`flex flex-1 min-h-11 min-w-11 flex-col items-center justify-center gap-0.5 px-1 py-1 text-center focus-ring-inset ${
                  isActive ? 'text-link' : 'text-ink-muted'
                }`}
              >
                {hasBadge ? (
                  <span className="relative inline-flex shrink-0">
                    <Icon className="size-6 shrink-0" aria-hidden="true" />
                    <CountBadge
                      count={badgeCount}
                      data-testid={`tab-badge-${tab.id}`}
                      ringClassName="ring-2 ring-surface"
                      className="absolute -right-3 -top-2"
                    />
                  </span>
                ) : (
                  <Icon className="size-6 shrink-0" aria-hidden="true" />
                )}
                <span className="type-caption break-words">{tab.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
